import { useEffect, useMemo } from "react";

import { addYears, differenceInMinutes, isAfter, parseISO } from "date-fns";

import { isValidHttpOrHttpsUrl } from "@/components/lib/url";
import { ComponentMetadata } from "@/components/pages_logged_in/program/components/componentMetadata";
import {
  hasInnerText,
  toParticipantOptions,
} from "@/components/pages_logged_in/program/components/set-components/configure-inputs/utils";
import {
  getMaxDate,
  getMinDate,
} from "@/components/pages_logged_in/program/components/utils";
import { validateComponentDate } from "@/components/pages_logged_in/program/components/validate-component-date";
import { ALL_PARTICIPANTS_OPTION } from "@/components/pages_logged_in/program/constants";
import {
  ComponentTypeEnum,
  InputMaybe,
  RoleplayRank,
  SelectableProgramComponentsQuery,
  SimpleProgramUsersQuery,
} from "@/graphql";
import {
  ComponentInput,
  CustomFormField,
  MainComponent,
} from "@/types/programs";
import { SelectOption } from "@/types/select";
import { NonNullableArrayItem } from "@/types/utils";
import { ensureNonNullArray } from "@/utils/array";

type SelectableProgramComponent = NonNullableArrayItem<
  SelectableProgramComponentsQuery["selectableProgramComponents"]
>;

interface DateTimeConstraintsResult {
  minLaunchDate: Date;
  maxLaunchDate: Date;
  minDueDate: Date;
  maxDueDate: Date;
  minEndDate: Date;
  maxEndDate: Date;
}

interface DateTimeConstraintsParams {
  launchDate?: string | null;
  dueDate?: string | null;
  endDate?: string | null;
  hasChangeStartDate: boolean;
  hasChangeDueDate: boolean;
  hasChangeEndDate: boolean;
}

export function useDateTimeConstraints({
  launchDate,
  dueDate,
  endDate,
  hasChangeStartDate,
  hasChangeDueDate,
  hasChangeEndDate,
}: DateTimeConstraintsParams): DateTimeConstraintsResult {
  const minLaunchDate =
    getMinDate([launchDate, new Date()], hasChangeStartDate) || new Date();

  const minDueDate = launchDate
    ? getMinDate([dueDate, launchDate], hasChangeDueDate) || new Date()
    : new Date();

  const minEndDate =
    launchDate || dueDate
      ? getMinDate([endDate, dueDate, launchDate], hasChangeEndDate) ||
        new Date()
      : new Date();

  const maxLaunchDate = addYears(new Date(), 1);

  const maxDueDate = endDate
    ? getMaxDate([endDate]) || new Date()
    : addYears(new Date(), 1);

  const maxEndDate = addYears(new Date(), 1);

  return {
    minLaunchDate,
    maxLaunchDate,
    minDueDate,
    maxDueDate,
    minEndDate,
    maxEndDate,
  };
}

const DEFAULT_PROMPT: Partial<Record<ComponentTypeEnum, string>> = {
  [ComponentTypeEnum.Video]: "Video Component",
  [ComponentTypeEnum.Article]: "Article Component",
  [ComponentTypeEnum.GroupMeeting]: "Group Meeting Component",
  [ComponentTypeEnum.Announcement]: "Announcement Component",
};

interface PopulateInputResult {
  isUseCustomFormField: boolean;
}

interface PopulateInputParams {
  input: ComponentInput;
  setName: (name: string) => void;
  setLaunchDate: (date: string) => void;
  setEndDate: (date: string) => void;
  setDueDate: (date: string) => void;
  setParticipantGuidance: (guidance: string) => void;
  setRequiredSendLaunchEmail: (required: boolean) => void;
  setRequiredSendDueEmail: (required: boolean) => void;
  setRequiredSendPastDueEmail: (required: boolean) => void;
  setVisibleUpcomingQuestions: (visible: boolean) => void;
  setCustomFormField: (field: CustomFormField) => void;
  setBody: (body: string) => void;
  setParticipantPrelaunchVisibility: (visible: boolean) => void;
  customFormField: CustomFormField;
  setParticipants: (participants: SelectOption[]) => void;
  participantOptions: SelectOption[];
  setDuration: (duration: number) => void;
  setIsDependent: (dependent: boolean) => void;
  setPrerequisiteComponent: (option?: SelectOption) => void;
  programComponentOptions: SelectOption[];
  setAutoLaunchWithProgram: (autoLaunchWithProgram: boolean) => void;
  programLaunchDate?: string | null;
  setOptional: (optional: boolean) => void;
  setScenarioRequiredNum: (value: number | null) => void;
  setScenarioAttemptMin: (value: number | null) => void;
  setScenarioAttemptMax: (value: number | null) => void;
  setScenarioRankMin: (value: InputMaybe<RoleplayRank> | null) => void;
  totalScenarios: number;
}

export const usePopulateInput = ({
  input,
  setName,
  setLaunchDate,
  setEndDate,
  setDueDate,
  setParticipantGuidance,
  setRequiredSendLaunchEmail,
  setRequiredSendDueEmail,
  setRequiredSendPastDueEmail,
  setVisibleUpcomingQuestions,
  setCustomFormField,
  setBody,
  setParticipantPrelaunchVisibility,
  customFormField,
  setParticipants,
  participantOptions,
  setDuration,
  setIsDependent,
  setPrerequisiteComponent,
  programComponentOptions,
  setAutoLaunchWithProgram,
  programLaunchDate,
  setOptional,
  setScenarioRequiredNum,
  setScenarioAttemptMin,
  setScenarioAttemptMax,
  setScenarioRankMin,
  totalScenarios,
}: PopulateInputParams): PopulateInputResult => {
  const isUseCustomFormField =
    input.type === ComponentTypeEnum.Video ||
    input.type === ComponentTypeEnum.Article ||
    input.type === ComponentTypeEnum.Announcement ||
    input.type === ComponentTypeEnum.GroupMeeting;

  useEffect(() => {
    if (input?.name) {
      setName(input.name);
    }

    if (input?.targetStartDate) {
      setLaunchDate(input.targetStartDate);
    }

    if (input?.targetEndDate) {
      setEndDate(input.targetEndDate);
      if (input.targetStartDate) {
        setDuration(
          differenceInMinutes(
            new Date(input.targetEndDate),
            new Date(input.targetStartDate),
          ),
        );
      }
    }

    if (input?.targetDueDate) {
      setDueDate(input.targetDueDate);
    }

    if (input?.participantGuidance) {
      setParticipantGuidance(input.participantGuidance);
    }

    if (typeof input.requiredSendLaunchEmail === "boolean") {
      setRequiredSendLaunchEmail(input.requiredSendLaunchEmail);
    }

    if (typeof input.requiredSendDueEmail === "boolean") {
      setRequiredSendDueEmail(input.requiredSendDueEmail);
    }

    if (typeof input.requiredSendPastDueEmail === "boolean") {
      setRequiredSendPastDueEmail(input.requiredSendPastDueEmail);
    }

    if (typeof input.visibleUpcomingQuestions === "boolean") {
      setVisibleUpcomingQuestions(input.visibleUpcomingQuestions);
    }

    if (
      typeof input?.autoLaunchWithProgram === "boolean" &&
      programLaunchDate &&
      isAfter(parseISO(programLaunchDate), new Date())
    ) {
      setAutoLaunchWithProgram(input.autoLaunchWithProgram);
    }
    if (input) {
      setOptional(input.optional ?? false);
    }

    if (input?.type === ComponentTypeEnum.AiRoleplay) {
      // not allow to null or undefined
      setScenarioRequiredNum(input.scenarioRequiredNum || totalScenarios || 1);

      if (input?.scenarioAttemptMin !== undefined) {
        setScenarioAttemptMin(input.scenarioAttemptMin);
      }
      if (input?.scenarioAttemptMax !== undefined) {
        setScenarioAttemptMax(input.scenarioAttemptMax);
      }
      if (input?.scenarioRankMin !== undefined) {
        setScenarioRankMin(input.scenarioRankMin);
      }
    }

    // init mode for announcement
    if (input?.type === ComponentTypeEnum.Announcement) {
      if (input?.body) {
        setBody(input.body);
      }
      if (typeof input.participantPrelaunchVisibility === "boolean") {
        setParticipantPrelaunchVisibility(input.participantPrelaunchVisibility);
      }
    }

    // video, article, group meeting, announcement
    // these components have only one form field (custom form field)
    if (isUseCustomFormField) {
      // edit mode: exist only one form field
      const formField = input.formFields?.[0];
      if (formField) {
        setCustomFormField(formField);
      } else {
        // init mode for video and article, group meeting, announcement
        // having no form field, set default prompt and id is null
        const prompt = input.type ? DEFAULT_PROMPT[input.type] : "";
        setCustomFormField({
          ...customFormField,
          prompt: prompt ?? "",
          id: undefined,
        });
      }
    }
  }, [input]);

  const participants = ensureNonNullArray(input.participants);
  const pendingParticipantEmails = ensureNonNullArray(
    input.pendingParticipantEmails,
  );

  useEffect(() => {
    if (input?.forAllParticipants) {
      setParticipants([ALL_PARTICIPANTS_OPTION]);
    } else if (participants.length) {
      setParticipants(
        participantOptions.filter(
          ({ value }) => value && participants.includes(value),
        ),
      );
    } else if (pendingParticipantEmails.length) {
      setParticipants(
        participantOptions.filter(
          ({ label }) => label && pendingParticipantEmails.includes(label),
        ),
      );
    } else if (participants.length === 0) {
      setParticipants([]);
    }
  }, [input, participantOptions]);

  // Set dependency status based on prerequisite component
  useEffect(() => {
    if (input?.prerequisiteComponentId && programComponentOptions?.length) {
      setIsDependent(true);
      setPrerequisiteComponent(
        programComponentOptions.find(
          ({ value }) => input.prerequisiteComponentId === value,
        ),
      );
    }
  }, [input?.prerequisiteComponentId, programComponentOptions]);

  return { isUseCustomFormField };
};

interface ConfigureOptionsParams {
  programUserData?: SimpleProgramUsersQuery;
  selectableProgramComponentData?: SelectableProgramComponentsQuery;
  input: ComponentInput;
  programComponent: MainComponent;
}

interface ConfigureOptionsResult {
  participantOptions: SelectOption[];
  programComponentOptions: SelectOption[];
}

export const useConfigureOptions = ({
  programUserData,
  selectableProgramComponentData,
  input,
  programComponent,
}: ConfigureOptionsParams): ConfigureOptionsResult => {
  const participantOptions = useMemo(
    () =>
      toParticipantOptions(
        ensureNonNullArray(programUserData?.simpleProgramUsers),
        ensureNonNullArray(programUserData?.pendingParticipantEmails),
      ),
    [programUserData],
  );

  const programComponentOptions = useMemo(
    () =>
      toProgramComponentOptions(
        ensureNonNullArray(
          selectableProgramComponentData?.selectableProgramComponents,
        ),
        input?.id,
        programComponent?.prerequisiteComponent,
      ),
    [
      selectableProgramComponentData,
      input?.id,
      programComponent?.prerequisiteComponent,
    ],
  );

  return {
    participantOptions,
    programComponentOptions,
  };
};

interface ValidationResult {
  canSubmit: boolean;
  hasChangeDueDate: boolean;
  hasChangeEndDate: boolean;
  hasChangeStartDate: boolean;
  message: string;
  errorType: string;
  valid: boolean;
}

interface ValidationParams {
  newInput: ComponentInput;
  programComponent: MainComponent;
  metaData: ComponentMetadata;
  saving: boolean;
  isValidEligibility: boolean;
  dueDate?: string | null;
  body: string;
  customFormField: CustomFormField;
  name: string;
  launchDate?: string | null;
  endDate?: string | null;
  participants: SelectOption[];
}

export const useValidation = ({
  newInput,
  programComponent,
  metaData,
  saving,
  isValidEligibility,
  dueDate,
  body,
  customFormField,
  name,
  launchDate,
  endDate,
  participants,
}: ValidationParams): ValidationResult => {
  const validateDateData = validateComponentDate(newInput, programComponent);

  const {
    hasChangeDueDate,
    hasChangeEndDate,
    hasChangeStartDate,
    message,
    errorType,
    valid,
  } = validateDateData;

  const isContentType =
    metaData.isSurvey ||
    metaData.isActionItems ||
    metaData.isVideo ||
    metaData.isArticle;

  const isMediaType =
    metaData.isVideo || metaData.isArticle || metaData.isGroupMeeting;

  const isValidAnnouncement = !metaData.isAnnouncement || hasInnerText(body);

  const isValidMedia =
    !isMediaType || isValidHttpOrHttpsUrl(customFormField?.link);

  const isValidName = !!name?.trim();

  const isValidDates = !!launchDate && !!endDate;

  const isValidContentDueDate = !isContentType || !!dueDate;

  const isValidParticipants = !!participants?.length;

  const canSubmit =
    isValidContentDueDate &&
    isValidAnnouncement &&
    isValidMedia &&
    isValidName &&
    isValidDates &&
    valid &&
    isValidParticipants &&
    !saving &&
    isValidEligibility;

  return {
    canSubmit,
    hasChangeDueDate,
    hasChangeEndDate,
    hasChangeStartDate,
    message: message || "",
    errorType: errorType || "",
    valid,
  };
};

type PrerequisiteComponent = MainComponent["prerequisiteComponent"] & {
  participantSubmitAt?: string | null;
};

const toProgramComponentOptions = (
  components: SelectableProgramComponent[],
  excludeId: string | null = "",
  prerequisiteComponent?: PrerequisiteComponent | null,
): SelectOption[] => {
  try {
    const options: SelectOption[] =
      components
        ?.filter(
          (component) =>
            component &&
            component.id !== excludeId &&
            component.parentComponent?.id !== excludeId,
        )
        ?.map((component) => ({
          value: component.id,
          label: [
            ComponentTypeEnum.OneOneCoachingIntro,
            ComponentTypeEnum.OneOneCoachingSession,
          ].includes(component.type)
            ? `${component.name} (${component.parentComponent?.name})`
            : component.name || "Unnamed Component",
        })) || [];

    if (
      prerequisiteComponent &&
      prerequisiteComponent.id &&
      components.length > 0 &&
      !components.some((item) => item.id === prerequisiteComponent.id)
    ) {
      options.push({
        value: prerequisiteComponent.id,
        label: prerequisiteComponent.name || "Unnamed Prerequisite",
      });
    }

    return options;
  } catch (error) {
    console.error("Error converting program components to options:", error);
    return [];
  }
};
